import { ApolloQueryResult } from '@apollo/client';
import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { BillingFrequency } from '@atlassian/cssxp-ccp-types';
import {
  FetchSandboxParentQuery,
  EntitlementDetails,
  LinkedDestinationEntitlement,
  ProvisionedResourceType,
  LinkedEntitlementTypes,
} from '@css-xp/graphtypes/src/generated-types-new';
import { getUserTier } from '@atlassian/cssxp-utils-entitlement';
import { sentenceCase } from '@atlassian/cssxp-utils-generic';
import { isStatuspage } from '@atlassian/cssxp-utils-product';
import {
  EntitlementIcon,
  SummaryPanelContainer,
  SummaryPanelIcon,
  SummaryPanelRow,
  useFeatureToggle,
} from '@css-xp/base';
import { ViewPricingDetails } from '@css-xp/entitlements-v2';
import { CloudSiteLink, NoData, OrgNameLink } from '@css-xp/ppc-common';
import { routePaths, STATSIG_FEATURE_GATES, ENABLE_DESIGN_ITERATION_1 } from '@css-xp/ppc-constants';
import { getChargeElement, getRouteUrl, isEnterpriseInstanceOffering, isRovoInstanceProduct } from '@css-xp/ppc-utils';
import { SandboxParent } from './sandbox-parent';
import { EntitlementParentProduct } from './entitlement-parent-product';
import './subscription-details-section.scss';

export const SubscriptionDetailsSection = ({
  entitlement,
  refetch,
  parentProductEntitlements,
  shouldShowAddOnDetails,
  sandboxParentDetails,
  isManuallyBilledSupportEnabled,
}: {
  entitlement: EntitlementDetails;
  refetch: () => Promise<ApolloQueryResult<unknown>>;
  parentProductEntitlements: LinkedDestinationEntitlement[];
  shouldShowAddOnDetails?: boolean;
  sandboxParentDetails?: {
    parent: FetchSandboxParentQuery | undefined;
    loading: boolean;
  };
  isManuallyBilledSupportEnabled?: boolean;
}) => {
  const {
    provisionedResourceDetails,
    cloudSiteHostname,
    cloudSiteId,
    offering,
    pricingPlan,
    billingFrequency,
    activeUsers,
    subscription,
    productId,
    isApp,
    isAddOn,
    isExternallyBilled,
  } = entitlement;
  const [isEnabled] = useFeatureToggle();
  const { txaId } = useParams<{ txaId: string }>();
  const invoicePath = getRouteUrl(routePaths.invoiceDetails, {
    txaId,
    invoiceId: '',
  });
  const isRovoPhase2Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_ROVO_IN_PPC_PHASE2);
  const isSandboxSupportForSubscriptionsEnabled = isEnabled(
    STATSIG_FEATURE_GATES.ENABLE_SANDBOX_SUPPORT_FOR_SUBSCRIPTIONS
  );
  const isDesignIteration1Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_DESIGN_ITERATION_1);
  const isEnterpriseInstanceEntitlement = isEnterpriseInstanceOffering(entitlement?.offering?.name);
  const shouldRenderRovoParent = isRovoPhase2Enabled && isRovoInstanceProduct(entitlement);
  const highestUsageParentEntitlement = useMemo(() => {
    return parentProductEntitlements?.sort((a, b) => Number(b?.activeUsers ?? 0) - Number(a?.activeUsers ?? 0))?.[0];
  }, [parentProductEntitlements]);

  const parentEntitlement = isApp && !isStatuspage(entitlement.productId) ? highestUsageParentEntitlement : null;

  const subscriptionsRoutePPC = getRouteUrl(routePaths.subscriptions, {
    txaId,
    invoiceId: '',
  });

  const shouldShowOrgInfo = useMemo(
    () =>
      provisionedResourceDetails !== null &&
      provisionedResourceDetails !== undefined &&
      provisionedResourceDetails?.provisionedResourceType === ProvisionedResourceType.ORGANISATION,
    [provisionedResourceDetails]
  );

  const shouldHideTotalAgents = useMemo(() => {
    return shouldShowAddOnDetails && isAddOn;
  }, [isAddOn]);

  const shouldHideUserTier = useMemo(() => {
    return shouldShowAddOnDetails && isAddOn;
  }, [isAddOn]);

  const shouldRenderParentProduct = isSandboxSupportForSubscriptionsEnabled ? isApp && !entitlement?.isSandbox : isApp;

  /** Temporary: Hide tooltip  */
  // const addOnTooltipContent = React.useMemo(() => {
  //   if (chargeElement === ChargeElementType.OBJECT) {
  //     return `Excludes 50,000 objects in the base plan`;
  //   } else if (chargeElement === ChargeElementType.VIRTUAL_AGENT) {
  //     return `Excludes 1,000 assisted conversations in the base plan`; // TODO: update it after the confirmation from the content team.
  //   }

  //   return null;
  // }, [chargeElement]);

  const sandboxParentEntitlementDetails = useMemo(() => {
    return sandboxParentDetails?.parent?.entitlementDetails;
  }, [sandboxParentDetails]);

  const shouldRenderSandboxParent = isSandboxSupportForSubscriptionsEnabled && entitlement?.isSandbox;

  return (
    <SummaryPanelContainer
      title={isDesignIteration1Enabled ? 'Billing overview' : 'Subscription overview'}
      iconKey={SummaryPanelIcon.SUBSCRIPTION_ICON}
      isViewMoreItemsVisibleByDefault={true}
      version={isDesignIteration1Enabled ? 2 : undefined}
    >
      {!isDesignIteration1Enabled && !shouldShowOrgInfo && (
        <SummaryPanelRow
          label="Cloud URL"
          value={<CloudSiteLink siteName={cloudSiteHostname} txaId={txaId} cloudSiteId={cloudSiteId} />}
          useWrapper={isDesignIteration1Enabled}
        />
      )}
      {shouldShowOrgInfo && (
        <>
          <SummaryPanelRow
            label="Org Id"
            value={provisionedResourceDetails?.organisationId}
            useWrapper={isDesignIteration1Enabled}
          />
          <SummaryPanelRow
            label="Org Name"
            value={
              <OrgNameLink
                orgName={provisionedResourceDetails?.organisationName}
                orgId={provisionedResourceDetails?.organisationId}
                txaId={txaId}
              />
            }
            useWrapper={isDesignIteration1Enabled}
          />
        </>
      )}
      {isEnterpriseInstanceEntitlement && (
        <SummaryPanelRow
          label="Parent enterprise plan"
          value={
            <EntitlementParentProduct
              entitlement={entitlement}
              destinationEntitlementType={[LinkedEntitlementTypes.ENTERPRISE_PARENT]}
            />
          }
          useWrapper={isDesignIteration1Enabled}
        />
      )}
      {shouldRenderRovoParent && (
        <SummaryPanelRow
          label="Parent"
          value={
            <EntitlementParentProduct
              entitlement={entitlement}
              destinationEntitlementType={[
                LinkedEntitlementTypes.PROD_ENTITLEMENT_APP,
                LinkedEntitlementTypes.PROD_ENTITLEMENT_PRODUCT,
                LinkedEntitlementTypes.SANDBOX_APP_ENTITLEMENT,
                LinkedEntitlementTypes.SANDBOX_PRODUCT_ENTITLEMENT,
                LinkedEntitlementTypes.PROD_ENTITLEMENT_ADDON,
              ]}
              findApps={true}
              findProducts={true}
            />
          }
          useWrapper={isDesignIteration1Enabled}
        />
      )}
      {!isDesignIteration1Enabled && shouldRenderParentProduct && (
        <SummaryPanelRow
          label="Parent product"
          value={
            parentEntitlement ? (
              <div className="parent-product-info">
                <span className="avatar-container" data-testid="parent-product-name">
                  <EntitlementIcon
                    productId={parentEntitlement.productId as string}
                    className="avatar medium"
                    isCloud
                  />
                  {parentEntitlement?.productName}
                </span>
                <Link
                  className="parent-product-link"
                  to={`${subscriptionsRoutePPC}${parentEntitlement.entitlementId}`}
                  data-testid="parent-product-link"
                >
                  {parentEntitlement.entitlementNumber}
                </Link>
              </div>
            ) : (
              <NoData />
            )
          }
          useWrapper={isDesignIteration1Enabled}
        />
      )}
      {!isDesignIteration1Enabled && (
        <SummaryPanelRow
          label="Product plan"
          value={offering?.name ? offering.name : <NoData />}
          useWrapper={isDesignIteration1Enabled}
        />
      )}
      <SummaryPanelRow
        label="Price plan eligibility"
        value={
          pricingPlan?.type ? (
            <div className="pricing-eligibility-section">
              <div>{sentenceCase(pricingPlan?.type)}</div>
              <div>
                <ViewPricingDetails
                  entitlement={entitlement}
                  transactionId={txaId}
                  refetch={refetch}
                  linkedEntitlements={parentProductEntitlements}
                  invoiceRedirectPath={invoicePath}
                  hidePricingCalculatorText
                />
              </div>
            </div>
          ) : (
            <NoData />
          )
        }
        useWrapper={isDesignIteration1Enabled}
      />
      {!isDesignIteration1Enabled &&
        (billingFrequency === BillingFrequency.ANNUAL || isExternallyBilled) &&
        !shouldHideUserTier && (
          <SummaryPanelRow
            label="User tier"
            value={!isStatuspage(productId) ? getUserTier(subscription) : <NoData />}
            isHiddenByDefault
            useWrapper={isDesignIteration1Enabled}
          />
        )}
      {!isDesignIteration1Enabled && !shouldHideTotalAgents && (
        <SummaryPanelRow
          label={`Total ${getChargeElement({ productId: productId, labelPlural: true })}`}
          value={Number(activeUsers) ? activeUsers : <NoData />}
          isHiddenByDefault
          useWrapper={isDesignIteration1Enabled}
        />
      )}
      {!isDesignIteration1Enabled && shouldShowAddOnDetails && isAddOn && (
        <>
          <SummaryPanelRow
            label={
              <div className="add-ons-usage-info-heading">
                <p className="add-on-usage-header-content"> Add-on usage </p>
                {/** Temporary : Hide tooltip */}
                {/* {addOnTooltipContent && (
                  <div>
                    <Tooltip content={addOnTooltipContent} tag="span" position="right">
                      <InfoIcon label="contacts-tooltip" testId="add-on-info-icon" size="small" />
                    </Tooltip>
                  </div>
                )} */}
              </div>
            }
            value={
              <div className="add-ons-usage-info-content">
                {billingFrequency === BillingFrequency.ANNUAL && (
                  <div className="add-ons-usage-max-quantity">
                    <div>Quantity:</div>
                    <div className="add-ons-usage-max-quantity-value" data-testid="add-on-usage-total-quantity">
                      {entitlement?.userTier ?? 0}
                    </div>
                  </div>
                )}

                <div className="add-ons-current-usage">
                  <div>Current:</div>
                  <div className="add-ons-current-usage-value" data-testid="add-on-usage-current-users">
                    {activeUsers ?? 0}
                  </div>
                </div>
              </div>
            }
            useWrapper={isDesignIteration1Enabled}
          />
        </>
      )}
      {!isDesignIteration1Enabled && shouldRenderSandboxParent && (
        <SummaryPanelRow
          label="Parent product"
          value={
            <SandboxParent
              entitlement={entitlement}
              parentDetails={sandboxParentEntitlementDetails}
              loading={sandboxParentDetails?.loading}
              isDesignIteration1Enabled={isEnabled(ENABLE_DESIGN_ITERATION_1)}
            />
          }
          useWrapper={isDesignIteration1Enabled}
        />
      )}
      {isManuallyBilledSupportEnabled && entitlement?.isExternallyBilled && (
        <>
          <SummaryPanelRow
            label="Contract ID"
            value={entitlement?.metadata?.contractId ?? <NoData />}
            isHiddenByDefault
            useWrapper={isDesignIteration1Enabled}
          />
          <SummaryPanelRow
            label="Contract type"
            value={entitlement?.metadata?.contractType?.replace('_', ' ') ?? <NoData />}
            isHiddenByDefault
            useWrapper={isDesignIteration1Enabled}
          />
        </>
      )}
    </SummaryPanelContainer>
  );
};
